import { useState } from "preact/hooks";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function toYmd(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function parseYmd(ymd: string): { year: number; month: number; day: number } {
  const [y, m, d] = ymd.split("-").map(Number);
  return { year: y, month: m - 1, day: d };
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function firstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export interface CalendarProps {
  /** Selected date as YYYY-MM-DD */
  value?: string;
  /** Called when a date is selected */
  onSelect?: (ymd: string) => void;
  /** Minimum selectable date as YYYY-MM-DD */
  min?: string;
  /** Maximum selectable date as YYYY-MM-DD */
  max?: string;
  className?: string;
}

export function Calendar({
  value,
  onSelect,
  min,
  max,
  className,
}: CalendarProps) {
  const initial = value ? parseYmd(value) : { year: new Date().getFullYear(), month: new Date().getMonth(), day: new Date().getDate() };
  const [viewYear, setViewYear] = useState(initial.year);
  const [viewMonth, setViewMonth] = useState(initial.month);

  const totalDays = daysInMonth(viewYear, viewMonth);
  const startDay = firstDayOfWeek(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const isDisabled = (day: number): boolean => {
    const ymd = toYmd(viewYear, viewMonth, day);
    if (min && ymd < min) return true;
    if (max && ymd > max) return true;
    return false;
  };

  const isSelected = (day: number): boolean => {
    if (!value) return false;
    return toYmd(viewYear, viewMonth, day) === value;
  };

  const isToday = (day: number): boolean => {
    const now = new Date();
    return (
      viewYear === now.getFullYear() &&
      viewMonth === now.getMonth() &&
      day === now.getDate()
    );
  };

  // Check if we can navigate to prev/next month (at least one day would be in range)
  const canGoPrev = (): boolean => {
    if (!min) return true;
    const prevMonthIdx = viewMonth === 0 ? 11 : viewMonth - 1;
    const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
    const lastDay = daysInMonth(prevYear, prevMonthIdx);
    return toYmd(prevYear, prevMonthIdx, lastDay) >= min;
  };

  const canGoNext = (): boolean => {
    if (!max) return true;
    const nextMonthIdx = viewMonth === 11 ? 0 : viewMonth + 1;
    const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;
    return toYmd(nextYear, nextMonthIdx, 1) <= max;
  };

  const cells: (number | null)[] = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  return (
    <div className={cn("tw:w-full tw:select-none", className)}>
      {/* Header: month/year nav */}
      <div className="tw:flex tw:items-center tw:justify-between tw:mb-2">
        <button
          type="button"
          onClick={prevMonth}
          disabled={!canGoPrev()}
          className="tw:p-1 tw:rounded-md tw:hover:bg-gray-100 tw:disabled:opacity-30 tw:disabled:cursor-not-allowed tw:cursor-pointer"
          aria-label="Previous month"
        >
          <ChevronLeft className="tw:size-4" style={{ color: "var(--so-text, #523D33)" }} />
        </button>
        <span
          className="tw:text-sm tw:font-semibold"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          disabled={!canGoNext()}
          className="tw:p-1 tw:rounded-md tw:hover:bg-gray-100 tw:disabled:opacity-30 tw:disabled:cursor-not-allowed tw:cursor-pointer"
          aria-label="Next month"
        >
          <ChevronRight className="tw:size-4" style={{ color: "var(--so-text, #523D33)" }} />
        </button>
      </div>

      {/* Day-of-week headers */}
      <div className="tw:grid tw:grid-cols-7 tw:mb-1">
        {DAY_NAMES.map((d) => (
          <div
            key={d}
            className="tw:text-center tw:text-xs tw:font-medium tw:text-gray-400 tw:py-1"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="tw:grid tw:grid-cols-7">
        {cells.map((day, i) => {
          if (day === null) {
            return <div key={`e-${i}`} />;
          }
          const disabled = isDisabled(day);
          const selected = isSelected(day);
          const today = isToday(day);

          return (
            <button
              key={day}
              type="button"
              disabled={disabled}
              onClick={() => {
                if (!disabled && onSelect) {
                  onSelect(toYmd(viewYear, viewMonth, day));
                }
              }}
              className={cn(
                "tw:relative tw:h-8 tw:w-full tw:text-sm tw:rounded-md tw:cursor-pointer tw:transition-colors",
                disabled &&
                  "tw:text-gray-300 tw:cursor-not-allowed tw:hover:bg-transparent",
                !disabled &&
                  !selected &&
                  "tw:hover:bg-[#FDEDEE] tw:text-[#523D33]",
                selected &&
                  "tw:bg-[#523D33] tw:text-[#FAC8CA] tw:font-semibold tw:hover:bg-[#523D33]/90",
                today && !selected && "tw:font-bold",
              )}
              style={
                !disabled && !selected
                  ? { color: "var(--so-text, #523D33)" }
                  : undefined
              }
            >
              {day}
              {today && !selected && (
                <span className="tw:absolute tw:bottom-0.5 tw:left-1/2 tw:-translate-x-1/2 tw:w-1 tw:h-1 tw:rounded-full tw:bg-[#523D33]" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
